import { useState } from 'react';
import { MDBTypography, MDBCheckbox, MDBIcon, MDBSpinner } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import Product3DModelUpload from '../../../components-dev-admin/product-3d/Product3DModelUpload';

export default function ProductMediaSection({
  productData,
  isAdminUser,
  companyId,
  handleSetImage,
  isUploadingImage,
  uploadingImageError,
  handleSetFile,
  isUploadingFile,
  uploadingFileError,
  pending3DModelFile,
  preview3DModelUrl,
  upload3DModelError,
  onFileSelected,
  onFileRemoved,
  handleConditionChange,
  getFieldError,
  markFieldTouched,
}) {
  const { t } = useTranslation();
  const [imageFileName, setImageFileName] = useState('');
  const [fileFileName, setFileFileName] = useState('');

  const imageError = getFieldError('image');
  const imageConditionError = getFieldError('imageCondition');
  const file3DModelConditionError = getFieldError('file3DModelCondition');
  const fileConditionError = getFieldError('fileCondition');

  const imageConditionChecked = productData?.conditions?.find(c => c.name === "image")?.accepted || false;
  const file3DModelConditionChecked = productData?.conditions?.find(c => c.name === "file3DModel")?.accepted || false;
  const fileConditionChecked = productData?.conditions?.find(c => c.name === "file")?.accepted || false;

  return (
    <>
      {/* Image Upload */}
      <div className="mb-2 mb-lg-4">
        <MDBTypography tag="h6" className="text-muted">
          {t("Upload an image for the product article")}
        </MDBTypography>
        <span className="text-muted small">{t("Recommended format: 16:9")}</span>
      </div>
      <div className="w-100 text-center mb-2 mb-lg-4">
        <img
          src={productData?.images?.main?.fileName
            ? "https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/" + productData.images.main.fileName
            : "https://placehold.co/600x400"}
          className="img-thumbnail bg-light"
          style={{ height: "300px", maxWidth: "100%", width: "100%", aspectRatio: "16 / 9", objectFit: "contain" }}
          alt="Product"
        />
      </div>
      {isUploadingImage ? (
        <div className="text-center py-2">
          <MDBSpinner size="sm" role="status" />
        </div>
      ) : (
        <label className="form-control d-flex align-items-center gap-2 text-muted" style={{ cursor: 'pointer' }} htmlFor="productImage">
          <MDBIcon fas icon="upload" />
          <span className="text-truncate">{imageFileName || t("Choose file")}</span>
          <input
            type="file"
            id="productImage"
            className="d-none"
            accept="image/*"
            onChange={e => {
              const file = e.target.files[0];
              if (file) setImageFileName(file.name);
              handleSetImage(file);
            }}
          />
        </label>
      )}
      {uploadingImageError && (
        <div className="text-danger small mt-1">{uploadingImageError}</div>
      )}
      {imageError && (
        <div className="text-danger small mt-1">{imageError}</div>
      )}
      <div className="mt-3">
        <MDBCheckbox
          name="image-condition"
          id="image-condition"
          className={imageConditionError ? "is-invalid" : ""}
          label={t("I agree that the image will be made public and we have the rights to use the image.")}
          checked={imageConditionChecked}
          onChange={(e) => handleConditionChange("image", e.target.checked)}
          onBlur={() => markFieldTouched('imageCondition')}
        />
        {imageConditionError && <div className="text-danger small mt-1">{imageConditionError}</div>}
      </div>

      {/* 3D Model (admin only) */}
      {isAdminUser && (
        <Product3DModelUpload
          file3DModel={productData?.file3DModel}
          pendingFile={pending3DModelFile}
          previewUrl={preview3DModelUrl}
          uploadError={upload3DModelError}
          conditionChecked={file3DModelConditionChecked}
          conditionError={file3DModelConditionError}
          onConditionChange={handleConditionChange}
          onConditionBlur={markFieldTouched}
          onFileSelected={onFileSelected}
          onFileRemoved={onFileRemoved}
        />
      )}

      {/* File Upload */}
      <div className="border border-secondary rounded-4 p-4 bg-light my-4">
        <div className="mb-3">
          <MDBTypography tag="h5" className="fw-bold">
            <MDBIcon fas icon="file-alt" className="me-2 text-primary" />
            {t("Optional: Upload a file for the product")}
          </MDBTypography>
        </div>
        {productData?.file?.url && (
          <p className="text-muted">
            {t("File uploaded successfully")}{' '}
            <a href={productData.file.url} target="_blank" rel="noopener noreferrer">
              <MDBIcon fas icon="file-pdf" className="me-1" />
              {productData.file.name || productData.file.fileName}
            </a>
          </p>
        )}
        {isUploadingFile ? (
          <div className="text-center py-2">
            <MDBSpinner size="sm" role="status" />
          </div>
        ) : (
          <label className="form-control d-flex align-items-center gap-2 text-muted" style={{ cursor: 'pointer' }} htmlFor="productFile">
            <MDBIcon fas icon="upload" />
            <span className="text-truncate">{fileFileName || t("Choose file")}</span>
            <input
              type="file"
              id="productFile"
              className="d-none"
              accept="application/pdf"
              onChange={e => {
                const file = e.target.files[0];
                if (file) setFileFileName(file.name);
                handleSetFile(file);
              }}
            />
          </label>
        )}
        {uploadingFileError && (
          <div className="text-danger small mt-1">{uploadingFileError}</div>
        )}
        <div className="mt-3">
          <MDBCheckbox
            name="script-condition"
            id="script-condition"
            className={fileConditionError ? "is-invalid" : ""}
            label={t("I confirm that I have the rights to use the file and that it can be made public.")}
            checked={fileConditionChecked}
            onChange={(e) => handleConditionChange("file", e.target.checked)}
            onBlur={() => markFieldTouched('fileCondition')}
          />
          {fileConditionError && <div className="text-danger small mt-1">{fileConditionError}</div>}
        </div>
      </div>
    </>
  );
}
